import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FCX, ID } from '@datassential/platform-ui-lib';
import { getSvgPath, Point } from './SocialLineChart.helpers';
import { SocialLineChartSeries } from './SocialLineChart';

interface SocialLineChartCurvesProps {
    series: SocialLineChartSeries[];
    columnsCount: number;
    labels: number[];
    height: number;
    width: number;
    activeSeriesId: ID;
    activeColumnIndex: number | null;
}

const PER_POINT_DURATION = 60;

const AnimatedCurve: React.FC<{
    s: SocialLineChartSeries;
    seriesIndex: number;
    points: Array<Point & { columnIndex: number }>;
    activeSeriesId: ID;
    activeColumnIndex: number | null;
}> = ({ s, seriesIndex, points, activeSeriesId, activeColumnIndex }) => {
    const pathRef = useRef<SVGPathElement | null>(null);
    const [pathLength, setPathLength] = useState(0);

    const pointCount = points.length;
    const totalDuration = PER_POINT_DURATION * Math.max(pointCount - 1, 1);

    useEffect(() => {
        if (pathRef.current) {
            setPathLength(pathRef.current.getTotalLength());
        }
    }, [points]);

    return (
        <g
            className={cn(
                'SocialLineChart__curve-container',
                activeSeriesId !== null && activeSeriesId !== s.id && 'is-dimmed',
            )}
        >
            <path
                ref={pathRef}
                fill="none"
                style={{
                    '--animation-duration': `${totalDuration}ms`,
                    '--stroke-dashoffset': `${pathLength}`,
                    '--stroke-dasharray': `${pathLength}`,
                } as React.CSSProperties}
                stroke={s.color}
                d={getSvgPath(points, false)}
                className={cn('SocialLineChart__curve', pathLength > 0 && 'is-ready')}
                data-testid={`blockSocialLineChartCurve_${seriesIndex}`}
            />
            {points.map((point, pIndex) => {
                const dotDelay = pointCount <= 1
                    ? 0
                    : (pIndex / (pointCount - 1)) * totalDuration;

                return (
                    <circle
                        key={`${s.id}-${point.columnIndex}`}
                        cx={point.x}
                        cy={point.y}
                        style={{
                            color: s.color,
                            animationDelay: `${dotDelay}ms`,
                        }}
                        stroke={s.color}
                        className={cn(
                            'SocialLineChart__dot',
                            'appears',
                            'fast',
                            activeColumnIndex === point.columnIndex && 'is-active',
                        )}
                        data-testid={`blockSocialLineChartDot_${seriesIndex}_${pIndex}`}
                    />
                );
            })}
        </g>
    );
};

const SocialLineChartCurves: FCX<SocialLineChartCurvesProps> = ({
    series,
    columnsCount,
    labels,
    height,
    width,
    activeSeriesId,
    activeColumnIndex,
}) => {
    const columnHalf = useMemo(
        () => width / (2 * columnsCount),
        [width, columnsCount]
    );
    const minValue = useMemo((): number => labels[labels.length - 1], [labels]);
    const maxValue = useMemo((): number => labels[0], [labels]);

    const getX = useCallback(
        (index: number): number => columnHalf * (2 * index + 1),
        [columnHalf]
    );
    const getY = useCallback(
        (value: number): number => {
            if (maxValue === minValue) return height / 2;
            return height * (1 - (value - minValue) / (maxValue - minValue));
        },
        [height, maxValue, minValue]
    );

    const seriesData = useMemo(
        () => series.map((s) => {
            const points: Array<Point & { columnIndex: number }> = [];

            s.values.forEach((value, columnIndex) => {
                if (value !== null) {
                    points.push({
                        x: getX(columnIndex),
                        y: getY(value),
                        columnIndex,
                    });
                }
            });

            return { series: s, points };
        }),
        [series, getX, getY]
    );

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
        >
            {seriesData.map(({ series: s, points }, seriesIndex) => (
                <AnimatedCurve
                    key={`${s.id}`}
                    s={s}
                    seriesIndex={seriesIndex}
                    points={points}
                    activeSeriesId={activeSeriesId}
                    activeColumnIndex={activeColumnIndex}
                />
            ))}
        </svg>
    );
};

export default SocialLineChartCurves;
